import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Slider, InputBase, ButtonBase } from '@mui/material';

export const PRICE_MIN = 0;
export const PRICE_MAX = 5000;

// The track uses a square-root scale: most per-SFT prices are well under ₹1,500, so a linear
// 0–5,000 track would squeeze most products into its first third. Endpoints stay exactly
// ₹0 and ₹5,000; the scale only changes how much track each price band gets.
// The top end means "₹5,000 and above", so pricier products are never silently excluded.
const POS_MAX = 1000;
const EXPONENT = 2;

const roundNice = (v) => {
  if (v < 1000) return Math.round(v / 10) * 10;
  if (v < 5000) return Math.round(v / 50) * 50;
  return Math.round(v / 100) * 100;
};

const formatINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const BUCKETS = 32;

// Everything that differs between range filters built on this component (bounds, labels,
// units, ticks, presets). PRICE_CONFIG is the default, so <PriceRangeFilter> is unchanged.
export const PRICE_CONFIG = {
  min: PRICE_MIN,
  max: PRICE_MAX,
  title: 'Price range',
  subtitle: 'per SFT',
  prefix: '₹',
  suffix: '',
  ariaNoun: 'price per SFT',
  // upper is true on the slider's top end, which means "and above"
  format: (v, upper) => `${formatINR(v)}${upper ? '+' : ''}`,
  formatTick: (v, upper) => (v >= 1000 ? `₹${+(v / 1000).toFixed(1)}k${upper ? '+' : ''}` : `₹${v}`),
  ticks: [0, 500, 2000, PRICE_MAX],
  presets: [
    { label: 'Under ₹250', range: [0, 250] },
    { label: '₹250 – ₹500', range: [250, 500] },
    { label: '₹500 – ₹1,000', range: [500, 1000] },
    { label: '₹1,000+', range: [1000, PRICE_MAX] },
  ],
};

const ACCENT = '#2C3E50';
const ACCENT_SOFT = 'rgba(44, 62, 80, 0.08)';

/**
 * Price range filter (per SFT) with a live product count.
 *
 * value     [min, max] in rupees
 * prices    effective prices of every product matching the other filters (not the price filter)
 * onChange  called while dragging (display only)
 * onCommit  called once with the final range (drag release, input blur/Enter, preset click)
 * config    bounds/labels/units (defaults to price); QuantityRangeFilter reuses this with SFT
 */
const PriceRangeFilter = ({ value, prices = [], loading = false, onChange, onCommit, config = PRICE_CONFIG }) => {
  const { min: RANGE_MIN, max: RANGE_MAX } = config;
  const clampPrice = (v) => Math.min(RANGE_MAX, Math.max(RANGE_MIN, Number(v) || 0));
  const toPos = (price) => Math.round(POS_MAX * Math.pow(clampPrice(price) / RANGE_MAX, 1 / EXPONENT));
  const toPrice = (pos) => {
    if (pos <= 0) return RANGE_MIN;
    if (pos >= POS_MAX) return RANGE_MAX;
    return clampPrice(roundNice(RANGE_MAX * Math.pow(pos / POS_MAX, EXPONENT)));
  };
  const formatFull = (v) => config.format(v, false);
  const formatUpper = (v) => config.format(v, v >= RANGE_MAX);

  const [minVal, maxVal] = Array.isArray(value) && value.length === 2
    ? [clampPrice(value[0]), clampPrice(value[1])]
    : [RANGE_MIN, RANGE_MAX];

  const [minText, setMinText] = useState(String(minVal));
  const [maxText, setMaxText] = useState(String(maxVal));
  useEffect(() => { setMinText(String(minVal)); }, [minVal]);
  useEffect(() => { setMaxText(String(maxVal)); }, [maxVal]);

  const validPrices = useMemo(
    () => prices.map(Number).filter((p) => Number.isFinite(p) && p >= 0),
    [prices]
  );

  const inRangeCount = useMemo(
    () => validPrices.filter((p) => p >= minVal && (maxVal >= RANGE_MAX || p <= maxVal)).length,
    [validPrices, minVal, maxVal, RANGE_MAX]
  );

  const buckets = useMemo(() => {
    const counts = Array(BUCKETS).fill(0);
    validPrices.forEach((p) => {
      const idx = Math.min(BUCKETS - 1, Math.floor((toPos(p) / POS_MAX) * BUCKETS)); // > max lands in the last bar
      counts[idx] += 1;
    });
    const peak = Math.max(...counts, 1);
    return counts.map((count, i) => ({
      count,
      // sqrt keeps a single-product bucket visible next to a tall one
      height: count === 0 ? 0 : 12 + 88 * Math.sqrt(count / peak),
      fromPos: (i / BUCKETS) * POS_MAX,
      toPos: ((i + 1) / BUCKETS) * POS_MAX,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validPrices, RANGE_MIN, RANGE_MAX]);

  const minPos = toPos(minVal);
  const maxPos = toPos(maxVal);
  const isFullRange = minVal === RANGE_MIN && maxVal === RANGE_MAX;

  const commit = (range) => {
    const lo = clampPrice(Math.min(range[0], range[1]));
    const hi = clampPrice(Math.max(range[0], range[1]));
    onCommit?.([lo, hi]);
  };

  const commitText = () => {
    const lo = minText === '' ? RANGE_MIN : clampPrice(parseInt(minText, 10));
    const hi = maxText === '' ? RANGE_MAX : clampPrice(parseInt(maxText, 10));
    setMinText(String(Math.min(lo, hi)));
    setMaxText(String(Math.max(lo, hi)));
    if (Math.min(lo, hi) !== minVal || Math.max(lo, hi) !== maxVal) commit([lo, hi]);
  };

  const inputBox = (label, text, setText) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        border: '1.5px solid #e2e8f0',
        borderRadius: '10px',
        bgcolor: '#fff',
        px: 1.25,
        py: 0.5,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:focus-within': { borderColor: ACCENT, boxShadow: `0 0 0 3px ${ACCENT_SOFT}` },
      }}
    >
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {config.prefix && (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748b', mr: 0.25 }}>{config.prefix}</Typography>
        )}
        <InputBase
          value={text}
          onChange={(e) => setText(e.target.value.replace(/[^\d]/g, '').slice(0, 5))}
          onBlur={commitText}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          inputProps={{ inputMode: 'numeric', 'aria-label': `${label} ${config.ariaNoun}` }}
          sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: '#0f172a', '& input': { p: 0 } }}
        />
        {config.suffix && (
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', ml: 0.5 }}>{config.suffix}</Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Header: title + live count */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '2px' }}>
            {config.title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{config.subtitle}</Typography>
        </Box>
        {!isFullRange && (
          <ButtonBase
            onClick={() => commit([RANGE_MIN, RANGE_MAX])}
            sx={{ fontSize: 12, fontWeight: 700, color: ACCENT, borderRadius: 1, px: 0.75, py: 0.25, '&:hover': { bgcolor: ACCENT_SOFT } }}
          >
            Reset
          </ButtonBase>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 0.75,
          mb: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: '10px',
          bgcolor: ACCENT_SOFT,
        }}
        aria-live="polite"
      >
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: ACCENT, lineHeight: 1 }}>
          {loading ? '…' : inRangeCount}
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
          {inRangeCount === 1 ? 'product' : 'products'} in {formatFull(minVal)} – {formatUpper(maxVal)}
          {!loading && validPrices.length > 0 && (
            <Box component="span" sx={{ color: '#94a3b8' }}> of {validPrices.length}</Box>
          )}
        </Typography>
      </Box>

      {/* Histogram of product prices; bars inside the selection are highlighted */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 56, px: '10px' }} aria-hidden>
        {buckets.map((b, i) => {
          const selected = b.toPos > minPos && b.fromPos < maxPos;
          return (
            <Box
              key={i}
              title={b.count ? `${b.count} product${b.count > 1 ? 's' : ''} · ${formatFull(toPrice(b.fromPos))} – ${formatUpper(toPrice(b.toPos))}` : undefined}
              sx={{
                flex: 1,
                height: `${b.height}%`,
                minHeight: b.count ? 3 : 0,
                borderRadius: '3px 3px 0 0',
                bgcolor: selected ? ACCENT : '#cbd5e1',
                opacity: selected ? 0.9 : 0.55,
                transition: 'background-color 0.15s, opacity 0.15s',
              }}
            />
          );
        })}
      </Box>

      <Slider
        value={[minPos, maxPos]}
        min={0}
        max={POS_MAX}
        step={1}
        disableSwap
        onChange={(e, pos) => onChange?.([toPrice(pos[0]), toPrice(pos[1])])}
        onChangeCommitted={(e, pos) => commit([toPrice(pos[0]), toPrice(pos[1])])}
        valueLabelDisplay="auto"
        valueLabelFormat={(pos) => formatUpper(toPrice(pos))}
        getAriaValueText={(pos) => formatFull(toPrice(pos))}
        getAriaLabel={(i) => (i === 0 ? `Minimum ${config.ariaNoun}` : `Maximum ${config.ariaNoun}`)}
        sx={{
          mt: '-6px',
          mx: '10px',
          width: 'calc(100% - 20px)',
          height: 4,
          color: ACCENT,
          py: '14px',
          '& .MuiSlider-rail': { bgcolor: '#e2e8f0', opacity: 1 },
          '& .MuiSlider-track': { border: 'none' },
          '& .MuiSlider-thumb': {
            width: 20,
            height: 20,
            bgcolor: '#fff',
            border: `3px solid ${ACCENT}`,
            boxShadow: '0 2px 6px rgba(15, 23, 42, 0.2)',
            '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 6px ${ACCENT_SOFT}` },
            '&.Mui-active': { boxShadow: `0 0 0 8px ${ACCENT_SOFT}` },
          },
          '& .MuiSlider-valueLabel': {
            bgcolor: ACCENT,
            borderRadius: '6px',
            fontSize: 11,
            fontWeight: 700,
            py: 0.25,
            px: 0.75,
          },
        }}
      />

      {/* Tick labels positioned on the (square-root) scale */}
      <Box sx={{ position: 'relative', height: 16, mx: '10px', mb: 1.5 }}>
        {config.ticks.map((t) => {
          const left = (toPos(t) / POS_MAX) * 100;
          return (
            <Typography
              key={t}
              component="span"
              sx={{
                position: 'absolute',
                left: `${left}%`,
                transform: t === RANGE_MIN ? 'none' : t === RANGE_MAX ? 'translateX(-100%)' : 'translateX(-50%)',
                fontSize: 10.5,
                fontWeight: 600,
                color: '#94a3b8',
                whiteSpace: 'nowrap',
              }}
            >
              {config.formatTick(t, t >= RANGE_MAX)}
            </Typography>
          );
        })}
      </Box>

      {/* Exact min / max entry */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {inputBox('Min', minText, setMinText)}
        <Box sx={{ width: 8, height: 2, bgcolor: '#cbd5e1', borderRadius: 1, flexShrink: 0 }} />
        {inputBox('Max', maxText, setMaxText)}
      </Box>

      {/* Quick ranges */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {config.presets.map((p) => {
          const active = p.range[0] === minVal && p.range[1] === maxVal;
          return (
            <ButtonBase
              key={p.label}
              onClick={() => commit(active ? [RANGE_MIN, RANGE_MAX] : p.range)}
              aria-pressed={active}
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                px: 1.25,
                py: 0.5,
                borderRadius: '999px',
                border: '1.5px solid',
                borderColor: active ? ACCENT : '#e2e8f0',
                bgcolor: active ? ACCENT : '#fff',
                color: active ? '#fff' : '#475569',
                transition: 'all 0.15s',
                '&:hover': { borderColor: ACCENT },
              }}
            >
              {p.label}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default PriceRangeFilter;
